import api from '../../api';
import Usuario from '../../model/Usuario/Usuario';
import { Permissao } from '../../model/Usuario/Permissao';

interface ApiUsuario {
    id: string;
    nome: string;
    userName: string;
    email: string;
    perfil: ApiPerfil;
}

interface ApiPerfil {
    id: string;
    nome: string;
    descricao: string;
    permissoes: string[];
}

export default class UsuarioViewModel {

    static async getUsuarios(): Promise<Usuario[]> {
        try {
            const response = await api.get('/usuarios');
            return response.data.map((apiUsuario: ApiUsuario) => UsuarioViewModel.mapApiUsuarioToUsuario(apiUsuario));
        } catch (error: unknown) {
            if (error instanceof Error) {
                const apiError = error as any;
                throw new Error(apiError.response?.data.message || 'Erro ao obter Usuários');
            }
            throw new Error('Erro desconhecido ao obter Usuários');
        }
    }

    static async getUsuario(id: string): Promise<Usuario> {
        try {
            const response = await api.get(`/usuarios/${id}`);
            return UsuarioViewModel.mapApiUsuarioToUsuario(response.data);
        } catch (error: unknown) {
            if (error instanceof Error) {
                const apiError = error as any;
                throw new Error(apiError.response?.data.message || 'Erro ao obter Usuário');
            }
            throw new Error('Erro desconhecido ao obter Usuário');
        }
    }

    static async updatePerfil(usuarioId: string, perfilId: string): Promise<Usuario> {
        try {
            if (perfilId === '') {
                const response = await api.patch(`/usuarios/${usuarioId}/perfis/remove`)
                return UsuarioViewModel.mapApiUsuarioToUsuario(response.data);
            }
            else {
                const response = await api.patch(`/usuarios/${usuarioId}/perfis/${perfilId}`)
                return UsuarioViewModel.mapApiUsuarioToUsuario(response.data);
            }

        } catch (error: unknown) {
            if (error instanceof Error) {
                const apiError = error as any;
                throw new Error(apiError.response?.data.message || 'Erro ao atualizar perfil do usuário');
            }
            throw new Error('Erro desconhecido ao atualizar perfil do usuário');
        }
    }

    static async getPermissoesUsuario(cpf: string): Promise<Permissao[]> {
        try {
            const response = await api.get(`/usuarios/${cpf}/permissoes`);
            const permissoesStrings: string[] = response.data;
            return permissoesStrings.map((nome) => ({
                nome,
            }));
        } catch (error: unknown) {
            if (error instanceof Error) {
                const apiError = error as any;
                throw new Error(apiError.response?.data.message || 'Erro ao obter permissões do usuário');
            }
            throw new Error('Erro desconhecido ao obter permissões do usuário');
        }
    }

    private static mapApiUsuarioToUsuario(apiUsuario: any): Usuario {
        return {
            id: apiUsuario.id,
            nome: apiUsuario.nome,
            username: apiUsuario.userName,
            cpf: '', // Mapear a propriedade cpf se disponível na resposta
            email: apiUsuario.email,
            perfil: apiUsuario.perfil ? {
                id: apiUsuario.perfil.id || '',
                nome: apiUsuario.perfil.nome || '',
                descricao: apiUsuario.perfil.descricao || '',
                permissoes: [] // Este campo pode ser preenchido posteriormente com getPermissoesUsuario
            } : {
                id: '',
                nome: 'sem perfil',
                descricao: '',
                permissoes: []
            },
            matricula: apiUsuario.matricula || null,
            log: '' // Mapear a propriedade log se disponível na resposta
        };
    }

    static async buscarUsuarioPorMatricula(matricula: string): Promise<Usuario> {
        try {
            const response = await api.get(`/usuarios/matricula/${matricula}`);
            return response.data;
        } catch (error) {
            throw new Error('Usuário não encontrado ou erro na busca');
        }
    }

    static async searchUsuarios(filtros: {
        nome?: string;
        email?: string;
        matricula?: string;
        perfil?: string;
    }, page: number = 0, size: number = 10) {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                size: size.toString(),
                sort: 'nome,asc'
            });

            if (filtros.nome) params.append('nome', filtros.nome);
            if (filtros.email) params.append('email', filtros.email);
            if (filtros.matricula) params.append('matricula', filtros.matricula);
            if (filtros.perfil) params.append('perfil', filtros.perfil);

            const response = await api.get(`/usuarios/search?${params}`);
            
            return {
                content: response.data._embedded?.usuarioResponseList?.map((apiUsuario: ApiUsuario) => 
                    UsuarioViewModel.mapApiUsuarioToUsuario(apiUsuario)) || [],
                pageMetadata: {
                    totalElements: response.data.page?.totalElements || 0,
                    totalPages: response.data.page?.totalPages || 0,
                    size: response.data.page?.size || size,
                    number: response.data.page?.number || page
                }
            };
        } catch (error) {
            console.error('Error searching usuarios:', error);
            throw error;
        }
    }
}
